import React from "react";
import { useNavigate } from "react-router-dom";
import GoogleAuthButton from "../../../components/auth/GoogleAuthButton";
import SignupAgeAffirmation from "../../../components/auth/SignupAgeAffirmation";

const AnonymousHero = () => {
  const navigate = useNavigate();

  return (
    <section
      // bg-position responds to breakpoint: on mobile the rooster lives on
      // the right side of hero-background2.png, so we anchor to `right` so
      // the cover-crop reveals him instead of cutting him off. On md+ the
      // hero is wide enough that a centered crop shows the whole scene.
      className="relative w-full overflow-hidden mb-8 rounded-3xl border border-border/80 bg-background bg-[url('/hero-background2.png')] bg-cover bg-no-repeat bg-right md:bg-center"
      aria-label="Welcome to Cluck"
    >
      {/* Mobile-only directional scrim. The bitmap is anchored to bg-right
          on small screens so the rooster reads, which puts the headline
          right on top of busy artwork, so a left-darkened gradient lifts the
          type without flattening the whole image. Hidden at md+ where the
          composition has room to breathe and the headline sits clear of
          the rooster. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none md:hidden"
        style={{
          background:
            "linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0) 100%)",
        }}
      />

      {/* Hard offset drop-shadow: solid black, no blur. Reads as a
          directional stroke rather than a haze. Offsets scale with the
          text size so the H1 gets a chunkier shadow than the eyebrow. */}
      <div className="relative p-6 md:p-10 lg:p-14 max-w-3xl">
        <p
          className="text-xs sm:text-sm uppercase tracking-[0.2em] text-primary font-semibold mb-3 sm:mb-4"
          style={{ textShadow: "1px 1px 0 #000" }}
        >
          1,000+ games · New games added daily
        </p>

        <h1
          className="text-white font-bold leading-[1.05] mb-4 sm:mb-5 text-4xl sm:text-5xl md:text-6xl"
          style={{ textShadow: "2px 2px 0 #000" }}
        >
          Play free.
          <br />
          Win real cash prizes.
        </h1>

        {/* Offer subhead. Leads with the redeemable prize story (why players
            join) and keeps the free routes as the supporting "how it's free"
            line, never the headline. Aligned to what a new account actually
            gets: 10,000 Gold Coins in the wallet, and Cluck Bucks on the day
            one welcome wheel (free to spin, phone verification required).
            Never state or imply a Cluck Bucks amount here, the wheel decides
            it. Prize / redemption language pairs with "No purchase
            necessary". */}
        <p
          className="text-white/90 text-base sm:text-lg leading-relaxed max-w-md mb-7 sm:mb-8"
          style={{ textShadow: "1px 1px 0 #000" }}
        >
          Spin the welcome wheel for Cluck Bucks and redeem them for real cash
          prizes. Free to play, with free Gold Coins every day. No purchase
          necessary.
        </p>

        <div className="flex flex-col gap-3 max-w-sm">
          {/* This is a SIGN UP entry point, so it opts into the inline 18+
              attestation (attestOver18) exactly like the signup page. The
              affirmation microcopy directly beneath is the point-of-action
              consent that carries over18_attested to the backend, so a Google
              signup started here skips the post-signup DobGate popup. */}
          <GoogleAuthButton label="Sign up with Google" attestOver18 />
          <SignupAgeAffirmation
            className="text-left"
            style={{ textShadow: "1px 1px 0 #000" }}
          />
          <button
            type="button"
            onClick={() => navigate("/signup")}
            className="text-muted-foreground hover:text-foreground text-sm underline underline-offset-4 cursor-pointer self-start"
            style={{ textShadow: "1px 1px 0 #000" }}
          >
            Use email instead
          </button>
        </div>

        {/* Payment-method trust strip sits below the CTAs as a quiet
            signal of which methods we accept. Cards are the IN side
            (Pay.com supports the standard 4); crypto is the OUT side
            for redemptions but is also accepted on the IN side, which
            is why both share the strip. The 5 prominent logos are the
            most-recognised tokens; "+ 5 more" covers LTC, ADA, MATIC,
            TRX, BASE. Stablecoins (USDC, USDT) are multi-chain, so the
            logos represent tokens we accept rather than specific chains.
            Card slots are placeholder solid blocks until brand assets
            land. Subdued opacity + small size keeps these as trust
            beats, not visual competition with the hero promise. */}
        <div className="mt-8 hidden sm:inline-flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl bg-black/45 px-5 py-3">
          <img
            src="/visa.png"
            alt="Visa"
            className="h-6 w-auto object-contain"
          />
          <img
            src="/mastercard.svg"
            alt="Mastercard"
            className="h-6 w-auto object-contain"
          />

          <span className="w-px h-5 bg-muted/50" aria-hidden="true" />

          <img
            src="/crypto/bitcoin-btc-logo.png"
            alt="Bitcoin"
            className="h-6 w-6 object-contain"
          />
          <img
            src="/crypto/ethereum-eth-logo.png"
            alt="Ethereum"
            className="h-6 w-6 object-contain"
          />
          <img
            src="/crypto/usd-coin-usdc-logo.png"
            alt="USDC"
            className="h-6 w-6 object-contain"
          />
          <img
            src="/crypto/tether-usdt-logo.png"
            alt="USDT"
            className="h-6 w-6 object-contain"
          />
          <img
            src="/crypto/solana-sol-logo.png"
            alt="Solana"
            className="h-6 w-6 object-contain"
          />

          <span className="text-xs text-foreground font-medium">+ 5 more</span>
        </div>

        {/* Hero footnote (currently hidden): fine print on redemption speed.
            Sits at the bottom of the hero so a redemption-timing disclaimer is
            visible alongside the promise rather than buried in the site footer.
            Same hard-offset shadow as the rest of the hero text so it stays
            legible against the artwork. */}
        <p
          className="hidden mt-6 text-[11px] leading-relaxed text-muted-foreground max-w-md"
          style={{ textShadow: "1px 1px 0 #000" }}
        >
          Around 85% of redemptions are processed within 2 hours. 15% may require
          human review to help protect customers against AML, fraud, and account
          security risks. Final delivery time depends on blockchain confirmation
          speed.
        </p>
      </div>
    </section>
  );
};

export default AnonymousHero;
